/**
 * NavigationHistoryContext — records the previous in-app location so detail
 * pages can offer a "Back" affordance that returns the user to where they
 * actually came from (Dashboard, search, a list) rather than a hardcoded
 * section list.
 *
 * Mounted once, high in the tree (inside the Router). Consumers read the
 * previous location via `usePreviousLocation()`; `useBackTarget` builds on it.
 */

import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

interface NavigationHistoryValue {
  /** Previous location as `pathname + search`, or null on first/deep-link load. */
  previous: string | null;
}

const NavigationHistoryContext = createContext<NavigationHistoryValue>({ previous: null });

export function NavigationHistoryProvider({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const currentRef = useRef<string | null>(null);
  const [previous, setPrevious] = useState<string | null>(null);

  const key = location.pathname + location.search;

  useEffect(() => {
    // On a genuine location change, the location that *was* current becomes
    // the previous one. The very first entry has no predecessor.
    if (currentRef.current !== null && currentRef.current !== key) {
      setPrevious(currentRef.current);
    }
    currentRef.current = key;
  }, [key]);

  return (
    <NavigationHistoryContext.Provider value={{ previous }}>
      {children}
    </NavigationHistoryContext.Provider>
  );
}

/** Previous in-app location (`pathname + search`), or null when there isn't one. */
// eslint-disable-next-line react-refresh/only-export-components -- non-component export alongside a component (affects dev fast-refresh only); file split is out of scope for CUR-1330
export function usePreviousLocation(): string | null {
  return useContext(NavigationHistoryContext).previous;
}
